import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import type { GroupRestaurant } from '../api/restaurantGroups';
import { groupReserveUrl } from '../utils/groupReserveUrl';

export interface GroupRestaurantCardProps {
  restaurant: GroupRestaurant;
  /**
   * Pre-validated button background color (passed through safeColor in B4).
   * When provided, overrides the default .btn-primary brand gold via inline style.
   */
  buttonBg?: string;
  /**
   * Pre-validated button text color (passed through safeColor in B4).
   * When provided, overrides the default .btn-primary brand foreground via inline style.
   */
  buttonText?: string;
}

/**
 * Card for a single restaurant within a group landing (Layout A).
 *
 * - Image: uses customStyle.bodyImage when present; otherwise a brand-muted
 *   gradient placeholder block — no broken <img> with a bad src.
 * - Address: rendered when present — thin divider then centered muted caption.
 *   address2 is shown on its own line when present and non-null.
 *   No address → neither divider nor address is rendered.
 * - Reserve pill: <a> styled as .btn-primary (gold, rounded-full). When
 *   buttonBg/buttonText are supplied (pre-validated via safeColor), they are
 *   applied as inline styles so they override the class-level token.
 * - Separation: shadow-sm + hairline border-border/60 + rounded-2xl for subtle
 *   premium lift on the themed surface.
 */
export function GroupRestaurantCard({ restaurant, buttonBg, buttonText }: GroupRestaurantCardProps) {
  const { t } = useTranslation('booking');

  const hasImage = Boolean(restaurant.customStyle?.bodyImage);
  const reserveUrl = groupReserveUrl(restaurant);

  // Build the pill's inline style only when custom theming is supplied.
  const pillStyle: React.CSSProperties =
    buttonBg || buttonText
      ? {
          ...(buttonBg ? { backgroundColor: buttonBg } : {}),
          ...(buttonText ? { color: buttonText } : {}),
        }
      : {};

  const hasAddress = Boolean(restaurant.address);

  return (
    <article
      data-testid="group-restaurant-card"
      data-verify-unit="GroupRestaurantCard"
      data-verify-has-image={String(hasImage)}
      data-verify-has-address={String(hasAddress)}
      data-verify-subdomain={restaurant.subdomainPrefix}
      className="bg-card rounded-2xl shadow-sm border border-border/60 flex flex-col overflow-hidden"
    >
      {/* Image region — 3:2 card ratio per design system */}
      <div className="relative w-full aspect-[3/2] overflow-hidden">
        {hasImage ? (
          <img
            src={restaurant.customStyle!.bodyImage}
            alt={restaurant.name}
            className="w-full h-full object-cover"
          />
        ) : (
          /* Placeholder: brand-muted gradient block — no broken src */
          <div
            aria-hidden="true"
            className="w-full h-full bg-gradient-to-br from-brand-muted to-brand-light"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 p-6">
        {/* Restaurant name — DM Serif Display per design system */}
        <h2 className="text-xl font-display text-foreground leading-tight">
          {restaurant.name}
        </h2>

        {/* Address — only when present: thin divider + muted caption */}
        {hasAddress && (
          <>
            <hr className="border-t border-border/40" />
            <div className="text-center" data-testid="group-restaurant-address">
              <p className="text-sm text-foreground/60">{restaurant.address}</p>
              {restaurant.address2 && (
                <p className="text-sm text-foreground/60">{restaurant.address2}</p>
              )}
            </div>
          </>
        )}

        {/* Reserve CTA — identical to the app's canonical primary button
            (IndexPage "Book a table"): shared <Button>, bg-brand, rounded-xl,
            Calendar icon, text-lg, shadow-lg → hover:shadow-xl. */}
        <Button
          asChild
          className="w-full py-6 text-lg gap-2 bg-brand hover:bg-brand-dark text-brand-foreground rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
        >
          <a
            href={reserveUrl}
            style={Object.keys(pillStyle).length > 0 ? pillStyle : undefined}
          >
            <Calendar size={20} aria-hidden="true" />
            {t('GROUP_RESERVE')}
          </a>
        </Button>
      </div>
    </article>
  );
}

export default GroupRestaurantCard;
